import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Typography,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { StyledDialogTitle } from "../common/StyledDialogTitle";
import { get_expense_category } from "../../features/expense/expenseCategoryService";
import {
  add_daily_expense,
  update_daily_expense,
} from "../../features/expense/expensesService";
import AppButton from "../common/AppButton";
import AppSnackbar from "../common/AppSnackbar";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CommonInputField from "../common/InputField/CommonInputField";

interface IProps {
  handleClose: () => void;
  initialData?: any;
  isEdit?: boolean;
  setReloadExpenses?: any;
  onUpdateSuccess?: () => void;
  onAddSuccess?: () => void;
}

interface Category {
  id: number;
  name: string;
}

const AddExpenseModal = ({
  handleClose,
  isEdit,
  initialData,
  setReloadExpenses,
  onUpdateSuccess,
  onAddSuccess,
}: IProps) => {
  const [expenseForm, setExpenseForm] = useState({
    expensename: isEdit ? initialData?.name : "",
    date: isEdit
      ? initialData?.expense_date?.slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    exp_category: isEdit ? initialData?.exp_category?.name : "",
    amount: isEdit ? initialData?.amount : "",
    description: isEdit ? initialData?.description : "",
  });
  const [formState, setFormState] = useState({
    openSnackbar: false,
    openUpdateSnackbar: false,
    openAddSnackbar: false,
  });

  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [catData, setCatData] = useState<Category[]>([]);

  const pharmacyId = parseInt(localStorage.getItem("pharmacyId") || "0");
  const getCategory = async () => {
    try {
      setIsLoading(true);
      const res = await get_expense_category({ page: 1, limit: 1000 }); // <-- added pagination params
      setCatData(res?.data.expense_categories || []);
    } catch (error: any) {
      alert(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCategory();
  }, []);

  const addExpense = async () => {
    const dto = {
      name: expenseForm.expensename,
      description: expenseForm.description,
      pharmacy_id: pharmacyId,
      amount: expenseForm.amount,
      exp_category_id:
        catData?.find((cat) => cat.name === expenseForm.exp_category)?.id || 1,
      date: new Date(expenseForm.date).toISOString(),
    };
    try {
      setIsLoading(true);
      const res = await add_daily_expense(dto);
      if (res) {
        // alert("Expense added successfully");
        setFormState((prev) => ({ ...prev, openAddSnackbar: true }));
        setReloadExpenses?.((prev: boolean) => !prev);
        handleClose();
        onAddSuccess?.();
      }
    } catch (error: any) {
      alert("Failed to add expense");
    } finally {
      setIsLoading(false);
    }
  };

  const updateExpense = async () => {
    const dto = {
      name: expenseForm.expensename,
      description: expenseForm.description,
      pharmacy_id: pharmacyId,
      amount: expenseForm.amount,
      exp_category_id:
        catData?.find((cat) => cat.name === expenseForm.exp_category)?.id || 1,
      exp_id: initialData?.id,
      date: new Date(expenseForm.date).toISOString(),
    };
    try {
      setIsLoading(true);
      const res = await update_daily_expense(dto);
      if (res) {
        // alert("Expense updated successfully");
        setFormState((prev) => ({ ...prev, openUpdateSnackbar: true }));
        setReloadExpenses?.((prev: boolean) => !prev);
        handleClose();
        onUpdateSuccess?.();
      }
    } catch (error: any) {
      alert("Failed to update expense");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    let updatedValue = value;

    if (field === "amount") {
      updatedValue = parseFloat(value) || 0;
    }

    if (field === "date") {
      updatedValue = value;
    }

    setExpenseForm((prev) => ({
      ...prev,
      [field]: updatedValue,
    }));
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);

    const requiredFields = ["expensename", "amount", "exp_category"];
    const hasErrors = requiredFields.some((field) => {
      const value = expenseForm[field as keyof typeof expenseForm];
      if (field === "amount") return isNaN(value as number);
      if (field === "exp_category") return !value.trim();
      return typeof value === "string" ? value.trim() === "" : false;
    });

    if (hasErrors) return;

    if (isEdit) {
      updateExpense();
    } else {
      addExpense();
    }
  };

  const isFieldValid = (field: string, required = false, pattern?: RegExp) => {
    const value = expenseForm[field as keyof typeof expenseForm];

    if (!required) return true;
    if (!isSubmitted && !touched[field]) return true;

    if (field === "amount") {
      const numericValue =
        typeof value === "string" ? parseFloat(value) : value;
      return !isNaN(numericValue) && numericValue > 0;
    }

    if (field === "exp_category") {
      return !!value.trim();
    }

    if (pattern && typeof value === "string") return pattern.test(value);
    return typeof value === "string" ? value.trim() !== "" : true;
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: "460px",
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: 24,
          position: "relative",
        }}
      >
        <StyledDialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
          isDone={false}
        >
          {isEdit ? "Update Expense" : "Add Expense"}
        </StyledDialogTitle>

        <Divider />
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <CommonInputField
                label="Expense Name"
                required
                placeholder="Enter Expense Name"
                value={expenseForm.expensename}
                onChange={(e) => handleChange("expensename", e.target.value)}
                onBlur={() => handleBlur("expensename")}
                isValid={isFieldValid("expensename", true)}
                error={!isFieldValid("expensename", true)}
                helperText={
                  !isFieldValid("expensename", true)
                    ? "Expense name is required"
                    : ""
                }
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 500, mb: 0.6 }}>
                Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={["day", "month", "year"]}
                  inputFormat="yyyy/MM/dd"
                  value={expenseForm.date ? new Date(expenseForm.date) : null}
                  onChange={(newValue) => {
                    const formattedDate = newValue
                      ? newValue.toISOString().slice(0, 10)
                      : null;
                    handleChange("date", formattedDate);
                  }}
                  maxDate={new Date()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "yyyy-mm-dd",
                        style: {
                          padding: "9px 12px",
                          fontSize: "12px",
                          color: "#333333",
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 500, mb: 0.5 }}>
                Expense Category
                <span style={{ color: "red" }}> *</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={expenseForm.exp_category}
                  error={!isFieldValid("exp_category", true)}
                  onChange={(e: SelectChangeEvent<string>) =>
                    handleChange("exp_category", e.target.value)
                  }
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#A1A1A1" }}>
                        {isEdit ? expenseForm.exp_category : "Select Expense"}
                      </span>
                    )
                  }
                  sx={{
                    height: "36px",
                    fontSize: "12px",
                    borderRadius: "5px",
                    color: "#333333",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",

                    "& .MuiSelect-select": {
                      padding: "9px 12px",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 200,
                        mt: 1,
                        overflowY: "auto",
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                >
                  <MenuItem disabled value="">
                    <span style={{ fontSize: "12px", color: "#65748B" }}>
                      {isEdit ? "Selected Expense" : "Select Expense"}
                    </span>
                  </MenuItem>
                  {catData?.map((category) => (
                    <MenuItem
                      key={category.id}
                      value={String(category.name)}
                      sx={{ fontSize: "14px", color: "#181818" }}
                    >
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>

                {!isFieldValid("exp_category", true) && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    Expense category is required
                  </Typography>
                )}
              </FormControl>
            </Box>

            <Box sx={{ flex: 1 }}>
              <CommonInputField
                label="Amount"
                required
                placeholder="e.g. 1500"
                value={expenseForm.amount.toString()}
                onChange={(e) => handleChange("amount", e.target.value)}
                onBlur={() => handleBlur("amount")}
                isValid={isFieldValid("amount", true)}
                error={!isFieldValid("amount", true)}
                helperText={
                  !isFieldValid("amount", true) ? "Amount is required" : ""
                }
                allowOnlyNumbers
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <CommonInputField
              label="Description"
              placeholder="Enter Description..."
              value={expenseForm.description}
              onChange={(e) => handleChange("description", e.target.value)}
              onBlur={() => handleBlur("description")}
              isValid={isFieldValid("description")}
              multiline
              rows={3}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <AppButton type="submit" title={isEdit ? "Update" : "Add"} />
          </Box>
        </Box>
        <AppSnackbar
          message="Updated Successfully!"
          open={formState.openUpdateSnackbar}
          handleClose={() =>
            setFormState((prev) => ({
              ...prev,
              openUpdateSnackbar: false,
            }))
          }
        />

        <AppSnackbar
          message="Added Successfully!"
          open={formState.openAddSnackbar}
          handleClose={() =>
            setFormState((prev) => ({
              ...prev,
              openAddSnackbar: false,
            }))
          }
        />
      </Box>
    </Box>
  );
};

export default AddExpenseModal;
